import { useEffect, useMemo } from "react";
import axios from "axios";
import type { QueryClient } from "@tanstack/react-query";
import {
  EntitiesResponse,
  EntityData,
  EntityPage,
  ExtendedUseQueryResult,
  ComputedFields
} from "./types";
import { isEmpty } from "lodash";
import { ENTITY_NAME_TO_PREFIX, EntityName } from "./entityPrefixes";

// Refetches one record's detail read after a save. The in-flight read is cancelled
// first: invalidation alone leaves a first read that has no data yet running, and
// that read predates the save.
export const refreshEntityDetail = (
  queryClient: QueryClient,
  entityName: string,
  id: string
) => {
  const queryKey = [entityName, { pathParams: { [entityName + "Ids"]: id } }];
  void queryClient.cancelQueries({ queryKey });
  void queryClient.invalidateQueries({ queryKey });
};

export const isValidEntityId = (id: string, entityName: string): boolean => {
  if (isEmpty(id)) return false;
  const prefix =
    ENTITY_NAME_TO_PREFIX[entityName as EntityName] as string | undefined;
  if (!prefix) return true;
  return id.startsWith(prefix);
};

export const useEntities = <T extends EntityData>(
  ids: string[],
  entityName: string,
  useStore: () => {
    addEntity: (entity: T) => void;
    getEntities: (ids: string[]) => (T | undefined)[];
  },
  useAPI: (
    endpoint: string,
    params: { pathParams: { [key: string]: string } },
    options: {
      enabled: boolean;
      computed_fields?: ComputedFields;
      refetchOnMount?: boolean | "always";
      staleTime?: number;
    }
  ) => ExtendedUseQueryResult<EntitiesResponse<T> | EntityPage<T>>,
  filterIds?: (ids: string[]) => string[],
  computed_fields?: ComputedFields,
  forceRefetch?: boolean
): {
  entities: (T | undefined)[];
  error: string | null;
  isSuccess: boolean;
  isError: boolean;
  isLoading: boolean;
  isPartial: boolean;
  missingIds: string[];
} => {
  const { addEntity, getEntities } = useStore();

  const validIds = useMemo(
    () => ids.filter((id) => isValidEntityId(id, entityName)),
    [ids, entityName]
  );

  const filteredIds = useMemo(
    () => (filterIds ? filterIds(validIds) : validIds),
    [validIds, filterIds]
  );

  const storedEntities = useMemo(
    () => (forceRefetch ? [] : getEntities(filteredIds)),
    [filteredIds, getEntities, forceRefetch]
  );

  const uncachedIds = useMemo(
    () =>
      filteredIds.filter(
        (id, i) => id !== null && id !== undefined && !storedEntities[i]
      ),
    [filteredIds, storedEntities]
  );

  // When computed fields are requested, we need to refetch even if entities exist in store
  // because the stored entities might not have the required computed fields
  const shouldFetchIds = useMemo(() => {
    if (computed_fields && computed_fields !== ComputedFields.NONE) {
      // If computed fields are requested, fetch all entities to ensure they have the computed fields
      return filteredIds.filter(
        (id) => id !== null && id !== undefined && !isEmpty(id)
      );
    }
    return uncachedIds.filter((id) => !isEmpty(id));
  }, [filteredIds, uncachedIds, computed_fields]);

  const apiParams =
    shouldFetchIds.length > 0
      ? { pathParams: { [entityName + "Ids"]: shouldFetchIds.join(",") } }
      : { pathParams: {} };

  const result = useAPI(entityName, apiParams, {
    enabled: shouldFetchIds.length > 0,
    computed_fields,
    // When forceRefetch is true, always refetch on mount and treat data as stale
    ...(forceRefetch && {
      refetchOnMount: "always" as const,
      staleTime: 0
    })
  });

  const [fetchedEntities, fetchError] = useMemo(() => {
    if (result.isSuccess && result.data) {
      const entities = (result.data as any)[entityName];
      if (Array.isArray(entities)) {
        return [entities as T[], null];
      } else {
        return [[], `No ${entityName} found in the response`];
      }
    }
    return [[], result.error ? String(result.error) : null];
  }, [result.isSuccess, result.data, result.error, entityName]);

  useEffect(() => {
    if (fetchedEntities.length > 0) {
      fetchedEntities.forEach(addEntity);
    }
  }, [fetchedEntities, addEntity]);

  const allEntities = useMemo(() => {
    const entityMap = new Map<string, T>();

    storedEntities.forEach((entity) => {
      if (entity) entityMap.set(entity.id, entity);
    });
    fetchedEntities.forEach((entity) => {
      entityMap.set(entity.id, entity);
    });

    return filteredIds
      .map((id) => entityMap.get(id))
      .filter((entity): entity is T => entity !== undefined);
  }, [filteredIds, storedEntities, fetchedEntities]);

  // Consider data loaded successfully if either:
  // 1. We have all entities in store/fetched results
  // 2. API call was successful (for cases where we needed to fetch)
  const isSuccess = useMemo(() => {
    const hasAllEntities = filteredIds.every(
      (id) =>
        storedEntities.some((entity) => entity?.id === id) ||
        fetchedEntities.some((entity) => entity.id === id)
    );

    // If we needed to fetch entities (either missing or for computed fields), we need the API call to be successful
    if (shouldFetchIds.length > 0) {
      return hasAllEntities && result.isSuccess;
    }

    // If everything was in store and no fetch was needed, we're successful
    return hasAllEntities;
  }, [
    filteredIds,
    storedEntities,
    fetchedEntities,
    shouldFetchIds.length,
    result.isSuccess
  ]);

  // Specific IDs were requested but the server returned none — treat as not-found.
  // Guard on !isFetching to avoid a flash-of-404 during stale-while-revalidate.
  const emptyFetchIsError = useMemo(
    () =>
      shouldFetchIds.length > 0 &&
      result.isSuccess &&
      !result.isFetching &&
      fetchedEntities.length === 0,
    [shouldFetchIds.length, result.isSuccess, result.isFetching, fetchedEntities.length]
  );

  const isError = result.isError || emptyFetchIsError;

  const isLoading = shouldFetchIds.length > 0 && result.isFetching;

  // Requested ids the server answered without: omitted from a 206, or none found (404).
  const isNotFound =
    axios.isAxiosError(result.error) && result.error.response?.status === 404;
  const missingIds = useMemo(() => {
    if (shouldFetchIds.length === 0 || !(result.isSuccess || isNotFound)) {
      return [];
    }
    const resolvedIds = new Set(allEntities.map((entity) => entity.id));
    return filteredIds.filter((id) => !resolvedIds.has(id));
  }, [shouldFetchIds.length, result.isSuccess, isNotFound, allEntities, filteredIds]);

  const isPartial =
    missingIds.length > 0 && missingIds.length < filteredIds.length;

  return {
    entities: allEntities,
    error: fetchError ?? (emptyFetchIsError ? `No ${entityName} found for the requested IDs` : null),
    isSuccess,
    isError,
    isLoading,
    isPartial,
    missingIds
  };
};
